import { createContext, FC, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";

import {
  Enterprise,
  EnterpriseAppState,
  NotificationMappingsState,
  Insights,
  MappingGroups,
  NotificationAction,
  NotificationMappingEntity,
  NotificationType
} from "@ctra/api";

import { isPending } from "@ctra/utils";

interface ContextType {
  allMappings: NotificationMappingsState;
  individualMappings: NotificationMappingsState;
  mappingGroups: MappingGroups;
  api: {
    updateNotificationSettings(
      channel: NotificationType,
      mappingId: NotificationMappingEntity["id"],
      isChecked: boolean
    ): void;
  };
  meta: {
    isLoading: boolean;
    updateID: NotificationMappingEntity["id"];
  };
}

const DefaultContext = createContext<ContextType>({
  allMappings: {},
  individualMappings: {},
  mappingGroups: {},
  api: {
    updateNotificationSettings: _.noop
  },
  meta: {
    isLoading: true,
    updateID: ""
  }
});

/**
 * Fetch insight mappings from the server
 * @param insightTypeID
 * @param children
 * @private
 */
const _InsightNotificationContextProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const [updateID, setupdateID] = useState<NotificationMappingEntity["id"]>("");

  const allMappings = useSelector<EnterpriseAppState, NotificationMappingsState>(
    Enterprise.entities.getNotificationMappings
  );

  const mappingGroups = useSelector<EnterpriseAppState, MappingGroups>(Enterprise.entities.getGroupMappings);

  const individualMappings = useSelector<EnterpriseAppState, NotificationMappingsState>(
    Enterprise.entities.getIndividualMappings
  );

  const isFetching = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, Insights.types.FETCH_INSIGHT_NOTIFICATION_MAPPINGS)
  );

  const isUpdating = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, Insights.types.UPDATE_INSIGHT_NOTIFICATION_SETTINGS)
  );

  useEffect(() => {
    if (_.isEmpty(allMappings)) {
      dispatch(Insights.actions.fetchInsightNotificationMappings.start());
    }
  }, [dispatch, allMappings]);

  /**
   * Update notification settings for the channels email & push
   * @param channel
   * @param mappingId
   * @param isChecked
   */
  const updateNotificationSettings = (
    channel: NotificationType,
    mappingId: NotificationMappingEntity["id"],
    isChecked: boolean
  ): void => {
    /**
     * Decides whether to enable or disable a notification type based on
     * whether the switch is checked or unchecked
     */
    const notificationAction = isChecked ? NotificationAction.enable : NotificationAction.disable;
    setupdateID(mappingId);
    dispatch(
      Insights.actions.updateInsightNotificationSettings.start(channel, mappingId, notificationAction)
    );
  };

  return (
    <DefaultContext.Provider
      value={{
        allMappings,
        individualMappings,
        mappingGroups,
        api: {
          updateNotificationSettings
        },
        meta: { isLoading: isFetching || isUpdating, updateID }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const InsightNotificationContext = {
  Provider: _InsightNotificationContextProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Grab the insight mappings from the context
 */
export const useInsightNotifications = (): ContextType => useContext(DefaultContext);
